import type { CookieConsentConfig } from "vanilla-cookieconsent"
import { denyAnalytics, grantAnalytics } from "../utils/analytics"

const config = (lang: string) => ({
	root: "#app",

	guiOptions: {
		consentModal: {
			layout: "box wide",
			position: "bottom right",
		},
		preferencesModal: {
			layout: "box",
		},
	},

	categories: {
		necessary: {
			readOnly: true,
			enabled: true,
		},
		analytics: {
			autoClear: {
				cookies: [{ name: /^(_ga|_gid)/ }],
			},
		},
	},

	// Fired the first time the user gives consent
	onFirstConsent: () => {},

	onConsent: (args) => {
		const currentConsent = args?.cookie?.categories || []

		if (currentConsent.includes("analytics")) {
			grantAnalytics()
		} else {
			denyAnalytics()
		}
	},

	onChange: (args) => {
		const currentConsent = args?.cookie?.categories || []

		if (currentConsent.includes("analytics")) {
			grantAnalytics()
		} else {
			denyAnalytics()
		}
	},

	language: {
		default: lang || "el",

		translations: {
			el: {
				consentModal: {
					title: "COOKIES",
					description: `
            Ο ιστότοπος www.valormovies.com χρησιμοποιεί cookies και παρόμοιες τεχνολογίες. 
            Ένα cookie είναι ένα μικρό αρχείο δεδομένων (αρχείο κειμένου) που ένας ιστότοπος ζητά από το πρόγραμμα περιήγησής σας να αποθηκεύσει στη συσκευή σας, ώστε να «θυμάται» πληροφορίες όπως τις προτιμήσεις γλώσσας ή τα στοιχεία σύνδεσής σας.
      
            Τα cookies που χρησιμοποιούμε μπορεί να είναι:
      
            α) Cookies πρώτου μέρους: Αυτά τα cookies ορίζονται από εμάς και είναι απαραίτητα για τη λειτουργία του ιστότοπου (π.χ. για σύνδεση, διατήρηση συνεδρίας, ρυθμίσεις απορρήτου).
      
            β) Cookies τρίτων: Χρησιμοποιούμε επίσης cookies τρίτων – τα οποία προέρχονται από διαφορετικό τομέα από τον τομέα του ιστότοπου που επισκέπτεστε – για στατιστικούς, διαφημιστικούς και μάρκετινγκ σκοπούς.
          `,
					acceptAllBtn: "Αποδοχή",
					acceptNecessaryBtn: "Απόρριψη",
					showPreferencesBtn: "Διαχείριση προτιμήσεων",
					closeIconLabel: "Απόρριψη όλων και κλείσιμο",
					footer: `<a href="/privacy-policy">Πολιτική Aπορρήτου</a>`,
				},
				preferencesModal: {
					title: "Προτιμήσεις Cookies",
					acceptAllBtn: "Αποδοχή όλων",
					acceptNecessaryBtn: "Απόρριψη όλων",
					savePreferencesBtn: "Αποθήκευση προτιμήσεων",
					sections: [
						{
							title: "Απολύτως απαραίτητα cookies",
							description: `
                Αυτά τα cookies είναι απαραίτητα για τη λειτουργία του ιστότοπου και δεν μπορούν να απενεργοποιηθούν στα συστήματά μας. 
                Συνήθως ορίζονται μόνο ως απάντηση σε ενέργειες που κάνετε εσείς και οι οποίες ισοδυναμούν με αίτημα για υπηρεσίες, όπως ο ορισμός των προτιμήσεων απορρήτου σας, η σύνδεση ή η συμπλήρωση φορμών. 
                Μπορείτε να ρυθμίσετε το πρόγραμμα περιήγησής σας να αποκλείει ή να σας ειδοποιεί για αυτά τα cookies, αλλά ορισμένα μέρη του ιστότοπου ενδέχεται να μην λειτουργούν σωστά. Αυτά τα cookies δεν αποθηκεύουν άμεσα προσωπικά αναγνωρίσιμα στοιχεία.
              `,
							linkedCategory: "necessary",
						},
						{
							title: "Analytics Cookies",
							description: `
                Αυτά τα cookies μας επιτρέπουν να μετράμε τις επισκέψεις και τις πηγές επισκεψιμότητας, ώστε να μπορούμε να μετράμε και να βελτιώνουμε την απόδοση του ιστότοπού μας. 
                Μας βοηθούν να γνωρίζουμε ποιες σελίδες είναι οι πιο δημοφιλείς και ποιες λιγότερο δημοφιλείς και να βλέπουμε πώς κινούνται οι επισκέπτες στον ιστότοπο. 
                Όλες οι πληροφορίες που συλλέγουν αυτά τα cookies είναι συγκεντρωτικές και, κατά κανόνα, ανώνυμες. Εάν δεν επιτρέψετε αυτά τα cookies, δεν θα γνωρίζουμε πότε έχετε επισκεφτεί τον ιστότοπό μας και δεν θα μπορούμε να παρακολουθούμε την απόδοσή του στον ίδιο βαθμό.
              `,
							linkedCategory: "analytics",
						},
					],
				},
			},
			en: {
				consentModal: {
					title: "COOKIES",
					description: `
            The website www.valormovies.com uses cookies and similar technologies. 
            A cookie is a small data file (text file) that a website asks your browser to store on your device to "remember" information such as your language preferences or login details.
      
            The cookies we use may include:
      
            a) First-party cookies: These cookies are set by us and are essential for the website to function (e.g., login, session maintenance, privacy settings).
      
            b) Third-party cookies: We also use third-party cookies — which come from a different domain than the one you are visiting — for analytics, advertising, and marketing purposes.
          `,
					acceptAllBtn: "Accept",
					acceptNecessaryBtn: "Reject",
					showPreferencesBtn: "Manage preferences",
					closeIconLabel: "Reject all and close",
					footer: `<a href="/privacy-policy">Privacy Policy</a>`,
				},
				preferencesModal: {
					title: "Cookie Preferences",
					acceptAllBtn: "Accept all",
					acceptNecessaryBtn: "Reject all",
					savePreferencesBtn: "Save preferences",
					sections: [
						{
							title: "Strictly necessary cookies",
							description: `
                These cookies are essential for the website to function and cannot be disabled in our systems. 
                They are usually set only in response to actions you take that constitute a request for services, such as setting your privacy preferences, logging in, or filling out forms. 
                You can configure your browser to block or alert you about these cookies, but parts of the site may not function properly. These cookies do not directly store personally identifiable information.
              `,
							linkedCategory: "necessary",
						},
						{
							title: "Performance cookies",
							description: `
                These cookies allow us to measure visits and traffic sources, so we can measure and improve the performance of our website. 
                They help us know which pages are the most or least popular and see how visitors move around the site. 
                All information collected by these cookies is aggregated and, generally, anonymous. 
                If you do not allow these cookies, we will not know when you visited our site and will not be able to track performance to the same extent.
              `,
							linkedCategory: "analytics",
						},
					],
				},
			},
		},
	},
})

export default config
